import type { BookingStage, DepositCollectionPoint, DepositRefundPoint } from '../api/bookings';
import type { SplitPart } from '../cash/split';
import type { PaymentMethod } from '../generated/enums';
import type { BookingDefaults, SignedInUser } from '../session/types';
import {
  formatPct,
  isTotalWithinCeiling,
  netRental,
  resolveAll,
  resolveDiscount,
} from './discount';
import type { UnitRateCard } from './pricing';
import { defaultHoldHours, holdHoursProblem } from './stageMoves';

/**
 * A booking being assembled at the counter, before it exists.
 *
 * <p>
 * <strong>The draft is a wearer list, not a garment list.</strong> A clerk taking a wedding booking
 * builds the party first and hangs each member's garments off them — which is how the conversation
 * actually goes, and it means the wearer is never a field somebody has to backfill once five
 * garments are out with three different people.
 * </p>
 *
 * <p>
 * All of it is plain data with pure functions over it, and none of it touches the server. That is
 * what lets the money update as the clerk picks dates without a round trip per keystroke, and it
 * keeps the awkward parts — a unit added twice, a deposit override, what blocks the next step —
 * testable without rendering anything.
 * </p>
 */

/** A garment picked out of the availability search. */
export interface DraftGarment {
  unitId: number;
  serialCode: string;
  /** The shop's own number, shown "RN-7" beside the serial; never sent. */
  referenceNumber?: string | null;
  itemId: number;
  itemName: string;
  /** What the slabs resolved to for this booking's charged period. Null when the card cannot price it. */
  rate: number | null;
  /**
   * This garment's own card, carried into the draft rather than looked up by design.
   *
   * <p>
   * Two copies of one design can price differently — a modifier on the garment, or a card written
   * against it — so a draft that re-priced by `itemId` when the dates moved would quietly charge
   * both at the same rate. Keeping the card on the garment also means re-pricing does not depend on
   * the availability search still being on screen.
   * </p>
   */
  rateCard: UnitRateCard | null;
  /**
   * This garment's own concession — "the sherwani is last season's, take ₹500 off that one".
   *
   * <p>
   * Both null is the ordinary case. Only one is set at a time; the control clears the other.
   * Composes with the draft's booking-level discount: lines come off their own rate first, then the
   * booking share applies to what is left, and the ceiling bounds the two together.
   * </p>
   */
  discountPct: number | null;
  discountAmount: number | null;
  /**
   * What goes out attached to this garment and has to come back.
   *
   * <p>
   * Empty is the ordinary case. These carry no `issued` flag: a booking says what the shop intends
   * to send, and only the issue counter can say what physically left. The settlement deduction
   * reads `issued && !returnedOn`, so a draft that claimed them issued would let a booking nobody
   * fulfilled produce a charge.
   * </p>
   */
  accessories: DraftAccessory[];
  packageId?: number | null;
  packageName?: string | null;
  /** The design's own name, without the variant — what the bag's photo row is headed with. */
  designName?: string;
  /** The garment's picture, for the bag's photo row; null draws the category's outline. */
  imageUrl?: string | null;
  /** The design's category, which picks the outline drawn when there is no picture. */
  categoryName?: string;
}

/** One accessory on a drafted garment. */
export interface DraftAccessory {
  /** Client-side only, like {@link DraftWearer.key} — the server assigns the real id. */
  key: string;
  name: string;
  /** Cufflinks are a pair counted as one row of two, so the charge below is per unit. */
  quantity: number;
  /** Null means the shop absorbs the loss rather than that it is free. Only a priced one deducts. */
  replacementCharge: number | null;
}

/** Somebody in the party. */
export interface DraftWearer {
  /** Client-side only. The server assigns real ids; this is what React keys on until it does. */
  key: string;
  name: string;
  relation: string;
  mobile: string;
  /** Set only if this person is on file. Null is the ordinary case for a groomsman. */
  customerId: number | null;
  garments: DraftGarment[];
  /**
   * When this person comes in to be fitted.
   *
   * On the wearer rather than the garment, and that is the whole reason the level exists: one
   * appointment covers everything Amit is wearing, while only his tuxedo sleeve goes to the tailor.
   * Collapsing the two is what puts a fitting time on a safa.
   */
  fittingAppointmentOn: string;
}

export interface BookingDraft {
  /** Who pays. Distinct from the wearers — the groom's father is often the customer. */
  customerId: number | null;
  customerName: string;
  /** Read back on the screens only; the booking is sent the id. */
  customerMobile: string;
  eventDate: string;
  eventKind: string;
  chargeFrom: string;
  chargeTo: string;
  possessionFrom: string;
  possessionTo: string;
  wearers: DraftWearer[];
  /** From §13's risk band. Overridable, and an override is recorded with a reason. */
  depositMultiplier: number;
  depositOverride: number | null;
  depositOverrideReason: string;
  /** Token as a percentage of the rental. Overridable to an absolute amount. */
  tokenPercent: number;
  tokenOverride: number | null;
  /**
   * How the money taken at confirmation is paid — the token and any deposit taken then, which go
   * in one press (`paymentsAtBooking`). Both clients recorded every token as cash until 2026-10-14,
   * which put a UPI token in the drawer.
   */
  payMethod: PaymentMethod;
  /**
   * What is taken now split across accounts (split payments step 6, owner 2026-11-01): a box per
   * account. Null — untouched — takes it all by `payMethod` into the method's default account, as
   * before. `takeBooking.ts` `paymentsAtBooking` turns the boxes into the token, then the deposit.
   */
  payParts?: SplitPart[] | null;
  /**
   * A concession off the hire (spec Section 4). Both null means none.
   *
   * <p>
   * Two fields rather than one, because shops say it both ways — "10% off" and "make it nine
   * thousand" — and which was said is worth keeping: a percentage follows the booking if the party
   * is later amended and a flat sum does not. Only one is set at a time; the panel clears the other.
   * </p>
   */
  discountPct: number | null;
  discountAmount: number | null;
  discountReason: string;
  /**
   * The largest this clerk may give at the acting branch, from `Role.MaxDiscountPct`.
   *
   * <p>
   * Carried in the draft rather than read from the session where it is used, so `draftProblems` can
   * say a concession is over the limit without the pure layer reaching for a React context. The
   * screen seeds it from the acting branch on the session — never from the user-level figure, which
   * is the tenant-wide roles alone and reads zero for a manager whose authority is per branch.
   * </p>
   */
  discountCeilingPct: number;
  /** Where the booking starts. Only the four pre-issue stages are offerable. */
  startAs: Extract<BookingStage, 'Enquiry' | 'Quotation' | 'Hold' | 'Confirmed'>;
  holdHours: number;
  /**
   * When the deposit is taken and when it goes back.
   *
   * Both ends configurable, because all six combinations are things real shops do — and the pair is
   * a property of the booking rather than of the flow, which is why they sit here and not in a
   * branch on the issue screen. They start as the shop's own (`emptyDraft`) and are always sent, so
   * the counter can say what will happen rather than "as the shop usually does".
   */
  depositCollectionPoint: DepositCollectionPoint;
  depositRefundPoint: DepositRefundPoint;
  /**
   * The shop's grace either side of the event, carried so a re-picked event day keeps it. Never
   * sent: the server is told the dates it produced.
   */
  collectBeforeDays: number;
  returnAfterDays: number;
  /** Counter notes on the booking as a whole. Per-garment notes are their own thing. */
  notes: string;
}

let nextKey = 0;

/**
 * A fresh client-side key. One counter across every kind of draft row, so two of them can never
 * collide however they are nested.
 */
function draftKey(prefix: string): string {
  nextKey += 1;

  return `${prefix}${nextKey}`;
}

/** Keys only have to be unique within one drawer's lifetime, which a counter makes cheap. */
export function wearerKey(): string {
  return draftKey('w');
}

/**
 * The key of the wearer who stands for the customer.
 *
 * <p>
 * A garment can go in the bag before anybody is named, when the customer question was skipped. It
 * hangs on this wearer, shown as "The customer" until {@link choosePayer} gives it the customer's
 * name. It is otherwise an ordinary wearer: renameable, removable, joined by the rest of the party.
 * </p>
 */
export const payerKey = 'payer';

/** Puts "the customer" at the front of the party, if they are not in it already. */
export function forTheCustomer(draft: BookingDraft): BookingDraft {
  if (draft.wearers.some((wearer) => wearer.key === payerKey)) {
    return draft;
  }

  return {
    ...draft,
    wearers: [
      {
        key: payerKey,
        name: draft.customerName,
        relation: '',
        mobile: '',
        customerId: null,
        garments: [],
        fittingAppointmentOn: '',
      },
      ...draft.wearers,
    ],
  };
}

/** What a wearer is called on screen: their name, or "The customer" before the customer is chosen. */
export function wearerLabel(wearer: Pick<DraftWearer, 'key' | 'name'>): string {
  if (wearer.name.trim()) {
    return wearer.name;
  }

  return wearer.key === payerKey ? 'The customer' : 'Unnamed';
}

/** What the product starts a booking from when the shop has set nothing — `BookingDefaults.cs`'s. */
export const productBookingDefaults: BookingDefaults = {
  depositMultiplier: 1.5,
  depositCollectionPoint: 'AtBooking',
  depositRefundPoint: 'AfterInspection',
  collectBeforeDays: 0,
  returnAfterDays: 0,
  issueRequiresPhoto: false,
};

/**
 * The shop's booking defaults off the session, or the product's when the server sent none (a phone
 * newer than its API). The one way either client reads them.
 */
export function bookingDefaultsOf(user: Pick<SignedInUser, 'bookingDefaults'>): BookingDefaults {
  return user.bookingDefaults ?? productBookingDefaults;
}

/**
 * The four dates around an event of one day or several.
 *
 * <p>
 * <strong>The charged days are the event days</strong>, and the grace either side only moves when
 * the garments are out. Shops price an event, not a day — "the jodhpuri is ₹3,000 for the wedding"
 * (the owner, 2026-10-14) — which a rate card says with one open band and no per-extra-day amount,
 * so the grace must never reach the charge. <strong>Both graces default to 0</strong>: a three-day
 * event is collected on its first day and back on its last. Until 2026-10-14 this padded a fixed day
 * each side of both spans, charging three days for a one-day event.
 * </p>
 */
export function suggestDates(
  eventFrom: string,
  {
    eventTo = eventFrom,
    collectBeforeDays = 0,
    returnAfterDays = 0,
  }: { eventTo?: string; collectBeforeDays?: number; returnAfterDays?: number } = {},
): Pick<BookingDraft, 'chargeFrom' | 'chargeTo' | 'possessionFrom' | 'possessionTo'> {
  const shift = (from: string, days: number): string => {
    const at = new Date(`${from.slice(0, 10)}T00:00:00Z`);

    if (Number.isNaN(at.getTime())) {
      return from;
    }

    at.setUTCDate(at.getUTCDate() + days);

    return at.toISOString().slice(0, 10);
  };

  return {
    chargeFrom: eventFrom,
    chargeTo: eventTo,
    possessionFrom: shift(eventFrom, -collectBeforeDays),
    possessionTo: shift(eventTo, returnAfterDays),
  };
}

/**
 * A tap on a day of the month: the event day, or — when the event is one day and the tap is later —
 * the last day of an event over several. Any other tap starts again on the day tapped, which is how
 * a range is picked on a phone and how a single day is moved.
 */
export function pickEventDay(draft: BookingDraft, day: string): Partial<BookingDraft> {
  const grace = {
    collectBeforeDays: draft.collectBeforeDays,
    returnAfterDays: draft.returnAfterDays,
  };
  const oneDay =
    draft.eventDate !== '' &&
    draft.chargeFrom === draft.eventDate &&
    draft.chargeTo === draft.eventDate;

  if (oneDay && day > draft.eventDate) {
    return suggestDates(draft.eventDate, { ...grace, eventTo: day });
  }

  return { eventDate: day, ...suggestDates(day, grace) };
}

/**
 * A fresh booking at this shop. The deposit's multiple and timing are the shop's own and stated, not
 * left null for the server to fill: the counter then says what will happen ("taken at pickup")
 * rather than "as the shop usually does", and a booking taken on it carries what was shown.
 */
export function emptyDraft(
  eventDate: string,
  defaults: BookingDefaults = productBookingDefaults,
): BookingDraft {
  const grace = {
    collectBeforeDays: defaults.collectBeforeDays,
    returnAfterDays: defaults.returnAfterDays,
  };

  return {
    customerId: null,
    customerName: '',
    customerMobile: '',
    eventDate,
    eventKind: '',
    ...suggestDates(eventDate, grace),
    ...grace,
    wearers: [],
    depositMultiplier: defaults.depositMultiplier,
    depositOverride: null,
    depositOverrideReason: '',
    tokenPercent: 40,
    tokenOverride: null,
    payMethod: 'Cash',
    discountPct: null,
    discountAmount: null,
    discountReason: '',

    // Zero until the screen fills it in from the acting branch. Erring closed: a draft that assumed
    // authority it could not confirm would offer a field the server then refuses.
    discountCeilingPct: 0,
    startAs: 'Confirmed',
    holdHours: defaultHoldHours,
    depositCollectionPoint: defaults.depositCollectionPoint,
    depositRefundPoint: defaults.depositRefundPoint,
    notes: '',
  };
}

/* ------------------------------------------------------------ the party */

/**
 * `fittingAppointmentOn` is optional here rather than required, because a wearer is added the
 * moment a name is typed and the appointment is arranged afterwards — often on a different day, by
 * a different person. Demanding it at the point of adding would put an empty date field in the way
 * of the one thing the row is for.
 */
export function addWearer(
  draft: BookingDraft,
  wearer: Omit<DraftWearer, 'key' | 'fittingAppointmentOn'> & { fittingAppointmentOn?: string },
): BookingDraft {
  return {
    ...draft,
    wearers: [...draft.wearers, { fittingAppointmentOn: '', ...wearer, key: wearerKey() }],
  };
}

/**
 * Records who is paying, and gives their name to the wearer standing for them.
 *
 * <p>
 * <strong>It never adds anybody to the party.</strong> {@link forTheCustomer} offers the customer
 * under their own name, and they join with their first garment. A wearer added here would be offered
 * beside that stand-in, the same name twice, and would refuse the father who pays and wears nothing
 * for having no garments.
 * </p>
 *
 * <p>
 * <strong>`customerId` stays null on the wearer</strong>, which is the rule the party keeps
 * everywhere: a wearer is measured and fitted and does not pay, and the payer's own record is on the
 * booking. Writing the id here would say this row was the customer, which is exactly the conflation
 * the nullable column exists to avoid.
 * </p>
 */
export function choosePayer(
  draft: BookingDraft,
  chosen: { id: number; name: string; mobile?: string } | null,
): BookingDraft {
  const named: BookingDraft = {
    ...draft,
    customerId: chosen?.id ?? null,
    customerName: chosen?.name ?? '',
    customerMobile: chosen?.mobile ?? '',
  };

  // The question can be skipped, so garments may already hang on "the customer" — that wearer takes
  // the customer's name, unless the clerk has typed another over it.
  const standIn = named.wearers.find((wearer) => wearer.key === payerKey);

  if (standIn) {
    const following = standIn.name === '' || standIn.name === draft.customerName;

    return following
      ? {
          ...named,
          wearers: named.wearers.map((wearer) =>
            wearer.key === payerKey ? { ...wearer, name: chosen?.name ?? '' } : wearer,
          ),
        }
      : named;
  }

  return named;
}

/** Corrects one wearer in place — the name, the relation, or when they come in to be fitted. */
export function updateWearer(
  draft: BookingDraft,
  key: string,
  changes: Partial<Omit<DraftWearer, 'key' | 'garments'>>,
): BookingDraft {
  return {
    ...draft,
    wearers: draft.wearers.map((wearer) =>
      wearer.key === key ? { ...wearer, ...changes } : wearer,
    ),
  };
}

export function removeWearer(draft: BookingDraft, key: string): BookingDraft {
  return { ...draft, wearers: draft.wearers.filter((wearer) => wearer.key !== key) };
}

/** Every unit already spoken for in this draft — what stops the same suit going to two people. */
export function claimedUnitIds(draft: BookingDraft): Set<number> {
  return new Set(
    draft.wearers.flatMap((wearer) => wearer.garments.map((garment) => garment.unitId)),
  );
}

/**
 * Adds a garment to a wearer, refusing one already claimed elsewhere in the draft.
 *
 * The availability engine will not have caught this: it answers "is this unit free on these dates",
 * and within one unsaved draft the answer is still yes. Two wearers holding the same tuxedo is a
 * mistake only the draft can see.
 */
export function addGarment(
  draft: BookingDraft,
  wearerKey: string,
  garment: DraftGarment,
): BookingDraft {
  if (claimedUnitIds(draft).has(garment.unitId)) {
    return draft;
  }

  return {
    ...draft,
    wearers: draft.wearers.map((wearer) =>
      wearer.key === wearerKey ? { ...wearer, garments: [...wearer.garments, garment] } : wearer,
    ),
  };
}

/**
 * Sets or clears one garment's own concession.
 *
 * Keyed on the unit, like {@link removeGarment}, because a draft's garments have no ids yet — the
 * server assigns those, and until it does the unit is the only thing that names a line.
 */
export function discountGarment(
  draft: BookingDraft,
  unitId: number,
  discount: { pct: number | null; amount: number | null },
): BookingDraft {
  return {
    ...draft,
    wearers: draft.wearers.map((wearer) => ({
      ...wearer,
      garments: wearer.garments.map((garment) =>
        garment.unitId === unitId
          ? { ...garment, discountPct: discount.pct, discountAmount: discount.amount }
          : garment,
      ),
    })),
  };
}

/**
 * Replaces one garment's accessory list.
 *
 * Keyed on the unit for the reason {@link discountGarment} is: a draft's garments have no ids yet,
 * so the unit is the only thing that names a line. One setter rather than add/update/remove
 * separately — the editor holds the whole list and states it, which keeps the three operations from
 * disagreeing about what an empty list means.
 */
export function setGarmentAccessories(
  draft: BookingDraft,
  unitId: number,
  accessories: DraftAccessory[],
): BookingDraft {
  return {
    ...draft,
    wearers: draft.wearers.map((wearer) => ({
      ...wearer,
      garments: wearer.garments.map((garment) =>
        garment.unitId === unitId ? { ...garment, accessories } : garment,
      ),
    })),
  };
}

/** A blank accessory line, ready for the clerk to name. */
export function emptyAccessory(): DraftAccessory {
  return { key: draftKey('a'), name: '', quantity: 1, replacementCharge: null };
}

/**
 * The accessory lines worth sending — the ones that have been named.
 *
 * <p>
 * A blank row is an editor artefact: the clerk pressed "Add" and has not typed yet, or thought
 * better of it. Sending it would be refused by the server, which requires a name, and failing a
 * whole booking because of an empty row nobody meant to add is the wrong trade at a counter.
 * </p>
 */
export function namedAccessories(garment: DraftGarment): DraftAccessory[] {
  return garment.accessories.filter((accessory) => accessory.name.trim().length > 0);
}

export function removeGarment(draft: BookingDraft, unitId: number): BookingDraft {
  return {
    ...draft,
    wearers: draft.wearers.map((wearer) => ({
      ...wearer,
      garments: wearer.garments.filter((garment) => garment.unitId !== unitId),
    })),
  };
}

/* ------------------------------------------------------------- the money */

export interface DraftMoney {
  /** **Gross** — what the rate card resolved, before any concession. */
  rental: number;
  /** Money off the hire, **both levels together**. Zero when none was given. */
  discount: number;
  /** The per-garment half of {@link discount}. */
  lineDiscount: number;
  /** The booking-level half, resolved against what is left once the lines have taken theirs. */
  bookingDiscount: number;
  /** {@link discount} as a percentage of {@link rental}. What the ceiling bounds — the total. */
  discountPct: number;
  /** Rental less the discount. What the token and the balance are computed from. */
  netRental: number;
  deposit: number;
  token: number;
  /** Owed at collection: the net rental less the token. The deposit follows its own collection point. */
  balance: number;
  /** True when a clerk typed over the suggested deposit — the reason then becomes required. */
  depositOverridden: boolean;
  /** True when the concession exceeds what this clerk's role allows. The server refuses it too. */
  discountOverCeiling: boolean;
  /** Garments the rate card could not price. A quote with one of these in it is not a quote. */
  unpriced: number;
}

/**
 * What the draft costs.
 *
 * <p>
 * <strong>An unpriced garment is counted, not treated as free.</strong> A rate card that cannot
 * price a booking is unfinished setup, and a total that quietly summed it as zero would hand out a
 * sherwani for nothing — the same distinction {@link formatAmount} draws between null and zero.
 * </p>
 */
export function draftMoney(draft: BookingDraft): DraftMoney {
  const garments = draft.wearers.flatMap((wearer) => wearer.garments);

  const rental = garments.reduce((sum, garment) => sum + (garment.rate ?? 0), 0);
  const unpriced = garments.filter((garment) => garment.rate === null).length;

  // Both levels in one pass: each garment's own concession comes off its own rate, then the
  // booking's applies to what is left. Same composition the server runs, and the reason the client
  // runs it at all is that the figure has to move while the clerk types.
  const resolved = resolveAll(
    garments.map((garment) => ({
      key: garment.unitId,
      rate: garment.rate ?? 0,
      input: { pct: garment.discountPct, amount: garment.discountAmount },
    })),
    { pct: draft.discountPct, amount: draft.discountAmount },
  );

  const discount = resolved.total;
  const net = netRental(rental, discount);

  // **The deposit multiplies the gross rental, not the discounted one**, matching the server. A
  // deposit is security against a garment coming back damaged or not at all, and what the shop
  // stands to lose is the garment — which a price concession has not made cheaper. Multiplying the
  // net would leave the most generously discounted bookings the least secured.
  const deposit = draft.depositOverride ?? Math.round(rental * draft.depositMultiplier);

  // The token *does* follow the discount, because it is a share of what the customer actually owes.
  const token = draft.tokenOverride ?? Math.round((net * draft.tokenPercent) / 100);

  return {
    rental,
    discount,
    lineDiscount: resolved.lineTotal,
    bookingDiscount: resolved.bookingAmount,
    discountPct: resolved.effectivePct,
    netRental: net,
    deposit,
    token,
    balance: Math.max(0, net - token),
    depositOverridden: draft.depositOverride !== null,

    // The *combined* figure against the ceiling. Checking the two levels separately would give a
    // clerk capped at 5% a real limit of 10%, reached by giving it at both.
    discountOverCeiling: !isTotalWithinCeiling(resolved, draft.discountCeilingPct),
    unpriced,
  };
}

/** What one garment's own concession comes to — for the row that shows it beside the rate. */
export function garmentDiscount(garment: DraftGarment): number {
  return (
    resolveDiscount(garment.rate ?? 0, {
      pct: garment.discountPct,
      amount: garment.discountAmount,
    })?.amount ?? 0
  );
}

/* -------------------------------------------------------- what blocks it */

/**
 * Which part of the screen can fix a problem.
 *
 * <p>
 * <strong>Five, where there were three, and the split is the whole point.</strong> These were
 * `customer | party | money` when the screen was a three-step wizard and the tag only had to name a
 * step to send the clerk back to. The wizard is gone and the money now folds into three independent
 * sections — deposit &amp; token, discount, take-it-as — each of which shows one summary line until
 * somebody opens it. A single `money` tag cannot say which of the three to open, so it would open
 * all three and the fold would be worth nothing.
 * </p>
 *
 * <p>
 * The rule each value has to keep: <strong>a problem is tagged with the place that can fix it</strong>.
 * That is why a negative discount typed on a garment is `party` — the garment row is in the left
 * column — while a negative one typed on the booking is `discount`. Tagging both the same would
 * force open a section holding neither the field nor the answer.
 * </p>
 */
export type DraftStep = 'customer' | 'party' | 'deposit' | 'discount' | 'stage';

export interface DraftProblem {
  step: DraftStep;
  message: string;
  /**
   * Which half of the customer step a problem belongs to — the payer or the dates, which are asked
   * on two screens, so each has to know which one can fix it.
   */
  part?: 'payer' | 'dates';
}

/**
 * Everything standing between this draft and a booking.
 *
 * Returned as a list rather than a boolean so the drawer can disable the right step's button and
 * say why, rather than greying out "Confirm" with no explanation — which is the version of this
 * that generates support calls.
 */
export function draftProblems(draft: BookingDraft): DraftProblem[] {
  const problems: DraftProblem[] = [];

  if (draft.customerId === null) {
    problems.push({
      step: 'customer',
      part: 'payer',
      message: 'Choose the customer who is paying.',
    });
  }

  if (!draft.eventDate) {
    problems.push({
      step: 'customer',
      part: 'dates',
      message: 'The event date anchors every other date.',
    });
  }

  if (draft.chargeFrom > draft.chargeTo) {
    problems.push({
      step: 'customer',
      part: 'dates',
      message: 'The charged period runs backwards.',
    });
  }

  // Possession has to cover the charge, or the shop is charging for days the garment is not out.
  if (draft.possessionFrom > draft.chargeFrom || draft.possessionTo < draft.chargeTo) {
    problems.push({
      step: 'customer',
      part: 'dates',
      message: 'Possession has to cover the whole charged period.',
    });
  }

  if (draft.wearers.length === 0) {
    problems.push({ step: 'party', message: 'Add at least one wearer.' });
  }

  for (const wearer of draft.wearers) {
    // "The customer" before the customer is chosen is named by choosing them, which the payer
    // problem above already asks for — saying it twice would send the clerk to the wrong place.
    const unnamedStandIn = wearer.key === payerKey && draft.customerId === null;

    if (!wearer.name.trim() && !unnamedStandIn) {
      problems.push({ step: 'party', message: 'Every wearer needs a name.' });
      break;
    }
  }

  const empty = draft.wearers.filter((wearer) => wearer.garments.length === 0);

  if (empty.length > 0) {
    problems.push({
      step: 'party',
      message: `${empty.map((wearer) => wearer.name || (wearer.key === payerKey ? 'The customer' : 'A wearer')).join(', ')} has no garments.`,
    });
  }

  const money = draftMoney(draft);

  if (money.unpriced > 0) {
    problems.push({
      step: 'party',
      message: `${money.unpriced} garment${money.unpriced === 1 ? ' has' : 's have'} no rate for this period.`,
    });
  }

  /*
    A typed deposit needs no reason, and that is deliberate.

    It did until 2026-08-30 — "an override is exactly the event a deposit-liability report needs to
    surface" — and the requirement was removed at the owner's direction, **on the server as well as
    here**, which is the only way to remove it: the client mirrors the server's rules, and dropping
    one side alone would offer a write the other refuses. The field is still there and still
    recorded; it is simply not demanded. The discount's reason is untouched and still compulsory.
  */

  /*
    A typed token has to be money the customer actually owes.

    `balance` clamps at zero, so an over-large token reads as "nothing left to pay" and says nothing
    at all about the excess — while the payment posted after the booking is the typed figure in
    full. That is a real overpayment recorded as a token, and the product has a separate payment
    kind for money beyond what is owed.

    Only when it was typed: the percentage buttons are a share of the net and cannot exceed it, and
    a rounding penny on 100% must not turn into a problem nobody can clear.
  */
  if (draft.tokenOverride !== null) {
    if (draft.tokenOverride < 0) {
      problems.push({ step: 'deposit', message: 'A token cannot be negative.' });
    } else if (draft.tokenOverride > money.netRental) {
      problems.push({
        step: 'deposit',
        message: 'The token is more than this booking comes to.',
      });
    }
  }

  if (money.discount > 0 && !draft.discountReason.trim()) {
    // Same argument as the deposit override: a concession with no sentence beside it is the line an
    // owner finds a month later with nobody able to say who agreed to it.
    problems.push({ step: 'discount', message: 'Say why the discount was given.' });
  }

  if (money.discountOverCeiling) {
    // Stated here rather than left to the server's 400, so the clerk finds out while the field is
    // still under their cursor instead of after the customer has been quoted.
    problems.push({
      step: 'discount',
      message: `That is ${formatPct(money.discountPct)}% off, and your limit is ${formatPct(
        draft.discountCeilingPct,
      )}%.`,
    });
  }

  // Two problems rather than one, because the two fields are in different places: the booking's own
  // concession is typed in the discount section and a garment's in the modal off its row. One shared
  // message would send half the people who hit it to a section with nothing wrong in it.
  if ((draft.discountPct ?? 0) < 0 || (draft.discountAmount ?? 0) < 0) {
    problems.push({ step: 'discount', message: 'A discount cannot be negative.' });
  }

  const negativeGarment = draft.wearers.some((wearer) =>
    wearer.garments.some(
      (garment) => (garment.discountPct ?? 0) < 0 || (garment.discountAmount ?? 0) < 0,
    ),
  );

  if (negativeGarment) {
    problems.push({ step: 'party', message: 'A garment discount cannot be negative.' });
  }

  // The server's own bounds on a hold — none is refused, and so is more than a week.
  const holdProblem = draft.startAs === 'Hold' ? holdHoursProblem(draft.holdHours) : null;

  if (holdProblem) {
    problems.push({ step: 'stage', message: holdProblem });
  }

  return problems;
}
